// Google Play Data Deletion page for FlexGym
import React from "react"
import { Mail, Phone, AlertCircle, CheckCircle, Clock } from "lucide-react"
import { Button } from "@/components/ui/button"

export default function DataDeletionPage() {
  const contactEmail = "support@flexgym.al"
  const contactPhone = "+355-69-318-2776"

  return (
    <main className="max-w-4xl mx-auto py-16 px-4 bg-background text-foreground">
      {/* Header with Logo */}
      <div className="text-center mb-12">
        <a href="/" className="flex justify-center mb-6">
          <img src="/logo-with-text.png" alt="THE NOT FIT Logo" className="h-20 w-auto" />
        </a>
        <h1 className="text-4xl font-bold mb-4 text-red-600">FlexGym Mobile App</h1>
        <h2 className="text-2xl font-semibold mb-2">Account Data Deletion Request</h2>
        <p className="text-lg text-muted-foreground">
          Request deletion of your FlexGym mobile app account and associated data
        </p>
      </div>

      {/* Steps to Request Account Deletion */}
      <section className="mb-12">
        <div className="flex items-center mb-6">
          <AlertCircle className="h-8 w-8 text-red-600 mr-3" />
          <h2 className="text-2xl font-bold">How to Request Account Deletion</h2>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <p className="text-lg mb-6 text-muted-foreground">
            To request deletion of your FlexGym mobile app account and data, please follow these steps:
          </p>
          
          <div className="space-y-6">
            {/* Step 1 */}
            <div className="flex items-start space-x-4">
              <div className="bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">
                1
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">Contact FlexGym Support</h3>
                <p className="text-muted-foreground mb-4">
                  Send a data deletion request using one of the following methods:
                </p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Mail className="h-5 w-5 text-red-600" />
                    <a href={`mailto:${contactEmail}?subject=Account Deletion Request - FlexGym Mobile App`} 
                       className="text-red-600 hover:underline font-medium">
                      {contactEmail}
                    </a>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Phone className="h-5 w-5 text-red-600" />
                    <a href={`tel:${contactPhone}`} 
                       className="text-red-600 hover:underline font-medium">
                      {contactPhone}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex items-start space-x-4">
              <div className="bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">
                2
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">Provide Required Information</h3>
                <p className="text-muted-foreground mb-2">
                  Include the following information in your deletion request:
                </p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                  <li>Your full name (as registered in the app)</li>
                  <li>Email address associated with your FlexGym account</li>
                  <li>Phone number (if different from contact number)</li>
                  <li>Reason for account deletion (optional)</li>
                  <li>Confirmation that you want all data permanently deleted</li>
                </ul>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex items-start space-x-4">
              <div className="bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">
                3
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">Verification Process</h3>
                <p className="text-muted-foreground">
                  Our team will verify your identity and process your request within 7 business days. 
                  You will receive a confirmation email once the deletion is complete.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Contact Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button asChild className="bg-red-600 hover:bg-red-700">
            <a href={`mailto:${contactEmail}?subject=Account Deletion Request - FlexGym Mobile App&body=Hello FlexGym Support,%0D%0A%0D%0AI would like to request the deletion of my FlexGym mobile app account and all associated data.%0D%0A%0D%0AAccount Details:%0D%0A- Full Name: [Your Name]%0D%0A- Email: [Your Email]%0D%0A- Phone: [Your Phone]%0D%0A%0D%0AI confirm that I want all my data permanently deleted.%0D%0A%0D%0AThank you.`}>
              <Mail className="h-4 w-4 mr-2" />
              Send Deletion Request Email
            </a>
          </Button>
          <Button variant="outline" asChild className="border-red-600 text-red-600 hover:bg-red-50">
            <a href={`tel:${contactPhone}`}>
              <Phone className="h-4 w-4 mr-2" />
              Call Support
            </a>
          </Button>
        </div>
      </section>

      {/* Data Types and Retention */}
      <section className="mb-12">
        <div className="flex items-center mb-6">
          <CheckCircle className="h-8 w-8 text-green-600 mr-3" />
          <h2 className="text-2xl font-bold">What Data Will Be Deleted</h2>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Data to be Deleted */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-xl font-semibold mb-4 text-green-600">Data That Will Be Permanently Deleted</h3>
            <ul className="space-y-2">
              <li className="flex items-center">
                <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0" />
                <span>Personal profile information (name, email, phone)</span>
              </li>
              <li className="flex items-center">
                <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0" />
                <span>Gym membership details and history</span>
              </li>
              <li className="flex items-center">
                <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0" />
                <span>Workout plans and exercise history</span>
              </li>
              <li className="flex items-center">
                <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0" />
                <span>Diet plans and nutritional data</span>
              </li>
              <li className="flex items-center">
                <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0" />
                <span>Appointment bookings and history</span>
              </li>
              <li className="flex items-center">
                <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0" />
                <span>Chat messages and communications</span>
              </li>
              <li className="flex items-center">
                <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0" />
                <span>App usage analytics and preferences</span>
              </li>
              <li className="flex items-center">
                <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0" />
                <span>Profile photos and uploaded content</span>
              </li>
            </ul>
          </div>

          {/* Data Retained */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-xl font-semibold mb-4 text-amber-600">Data That May Be Retained</h3>
            <ul className="space-y-2">
              <li className="flex items-center">
                <AlertCircle className="h-4 w-4 text-amber-600 mr-2 flex-shrink-0" />
                <span>Financial transaction records (for legal compliance)</span>
              </li>
              <li className="flex items-center">
                <AlertCircle className="h-4 w-4 text-amber-600 mr-2 flex-shrink-0" />
                <span>Anonymized usage statistics (no personal identifiers)</span>
              </li>
              <li className="flex items-center">
                <AlertCircle className="h-4 w-4 text-amber-600 mr-2 flex-shrink-0" />
                <span>Legal and compliance records (as required by law)</span>
              </li>
            </ul>
            
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Some data may be retained for legal, regulatory, or security purposes 
                as required by applicable laws. This data is kept secure and is not used for any 
                other purposes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Retention Period */}
      <section className="mb-12">
        <div className="flex items-center mb-6">
          <Clock className="h-8 w-8 text-blue-600 mr-3" />
          <h2 className="text-2xl font-bold">Data Retention Timeline</h2>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl font-bold text-blue-600">7</span>
              </div>
              <h3 className="font-semibold mb-2">Days</h3>
              <p className="text-sm text-muted-foreground">Request processing time</p>
            </div>
            
            <div className="text-center">
              <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl font-bold text-green-600">30</span>
              </div>
              <h3 className="font-semibold mb-2">Days</h3>
              <p className="text-sm text-muted-foreground">Complete deletion of personal data</p>
            </div>
            
            <div className="text-center">
              <div className="bg-amber-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl font-bold text-amber-600">7</span>
              </div>
              <h3 className="font-semibold mb-2">Years</h3>
              <p className="text-sm text-muted-foreground">Financial records (legal requirement)</p>
            </div>
          </div>
        </div>
      </section>

      {/* Important Notes */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-6">Important Information</h2>
        
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-800 mb-1">Account Deletion is Permanent</h3>
                <p className="text-red-700 text-sm">
                  Once your account is deleted, it cannot be recovered. You will need to create a new account 
                  if you wish to use FlexGym services again.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <CheckCircle className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-800 mb-1">Active Memberships</h3>
                <p className="text-blue-700 text-sm">
                  If you have an active gym membership, please contact the gym directly to discuss 
                  cancellation terms before requesting account deletion.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start">
              <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-green-800 mb-1">Data Export</h3>
                <p className="text-green-700 text-sm">
                  Before deletion, you can request a copy of your data. Contact us if you need 
                  to export your information before account deletion.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="text-center">
        <h2 className="text-2xl font-bold mb-4">Need Help?</h2>
        <p className="text-muted-foreground mb-6">
          If you have questions about data deletion or need assistance, our support team is here to help.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
          <Button variant="outline" asChild>
            <a href={`mailto:${contactEmail}`}>
              <Mail className="h-4 w-4 mr-2" />
              Email Support
            </a>
          </Button>
          <Button variant="outline" asChild>
            <a href={`tel:${contactPhone}`}>
              <Phone className="h-4 w-4 mr-2" />
              Call Support
            </a>
          </Button>
        </div>
        
        <div className="text-center">
          <a href="/privacy" className="text-red-600 hover:underline mr-4">Privacy Policy</a>
          <a href="/support" className="text-red-600 hover:underline mr-4">Support Center</a>
          <a href="/" className="text-red-600 hover:underline">Back to FlexGym</a>
        </div>
      </section>

      {/* Footer */}
      <footer className="mt-16 pt-8 border-t border-border text-center text-sm text-muted-foreground">
        <p>© 2025 FlexGym. This page complies with Google Play Store data deletion requirements.</p>
        <p className="mt-2">Last updated: August 30, 2025</p>
      </footer>
    </main>
  )
}
